<template>
  <!-- Template element -->
  <vue-draggable-resizable
    v-if="image"
    ref="templateArea"
    class-name="crop-area"
    class-name-handle="corner"
    :min-width="50"
    :min-height="50"
    :max-width="maxWidth"
    :max-height="maxHeight"
    :w="size.width * scaledTemplateSize"
    :h="size.height * scaledTemplateSize"
    :x="position.x"
    :y="position.y"
    :parent="dragModeText"
    :handles="['tl', 'tr', 'bl', 'br']"
    :lock-aspect-ratio="lockRatio"
    @dragging="handleDragging"
    @resizing="handleResizing"
  >
    <img :src="image.src" class="template-img" @load="updateImage" />
  </vue-draggable-resizable>
</template>

<script>
import VueDraggableResizable from "vue-draggable-resizable";

// @group Areas
// Draggable & resizable image container
export default {
  name: "TemplateArea",
  components: {
    VueDraggableResizable
  },
  model: {
    prop: "position",
    event: "move"
  },
  props: {
    scaledTemplateSize: {
      type: Number,
      default: 0
    },
    dragModeText: {
      type: Boolean,
      default: false
    },
    // x, y (in pixels)
    position: {
      type: Object,
      required: true
    },
    // Image data
    image: {
      type: HTMLImageElement,
      required: true
    },
    // Maximum width (in pixels)
    maxWidth: {
      type: Number,
      required: false,
      default: null
    },
    // Maximum height (in pixels)
    maxHeight: {
      type: Number,
      required: false,
      default: null
    }
  },
  data() {
    return {
      size: {
        width: 50,
        height: 50
      },
      // Save sides aspect ratio flag
      lockRatio: true
    };
  },
  computed: {
    // @vuese
    // Are maximum size parameters inited
    maxInited() {
      return this.maxWidth && this.maxHeight;
    }
  },
  watch: {
    size() {
      this.$emit("resize", this.size);
    },
  },
  methods: {
    // @vuese
    // Update area placement when new image uploaded
    updateImage() {
      let w = this.image.width;
      let h = this.image.height;

      // if image size is out of bounds, then fit image to bounding rectangle
      if (this.maxInited) {
        if (w > this.maxWidth || h > this.maxHeight) {
          const bordersRatio = this.maxWidth / this.maxHeight;
          const imageRatio = w / h;

          if (imageRatio > bordersRatio) {
            // image more horizontal then bounding rectangle
            h *= this.maxWidth / w;
            w = this.maxWidth;
          } else {
            // image more vertical then bounding rectangle
            w *= this.maxHeight / h;
            h = this.maxHeight;
          }
        }
      }

      /** free aspect ratio locking temporary */
      this.lockRatio = false;
      this.$nextTick(() => {
        this.lockRatio = true;
      });

      this.size = {
        width: w,
        height: h
      };
    },
    updatePosition(x, y) {
      // Image moved
      this.$emit("move", { x, y });
    },
    handleDragging(x, y) {
      this.updatePosition(x, y);
    },
    handleResizing(x, y, width, height) {
      this.updatePosition(x, y);
      this.size = {
        width,
        height
      };
    }
  }
};
</script>

<style scoped>
.crop-area {
  /* Absolute positioned area will be place over the image */
  position: absolute;
  top: 0;
  left: 0;

  cursor: move;
  background-color: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.3);
}
.template-img {
  width: 100%;
  height: 100%;
}
</style>

<style>
.corner {
  width: 20px;
  height: 20px;
  border: 4px solid rgba(255, 255, 255, 0.2);
}
.corner-tr {
  position: absolute;
  top: -2px;
  right: -2px;

  border-bottom-style: none;
  border-left-style: none;

  cursor: ne-resize;
}
.corner-tl {
  position: absolute;
  top: -2px;
  left: -2px;

  border-bottom-style: none;
  border-right-style: none;

  cursor: nw-resize;
}
.corner-br {
  position: absolute;
  bottom: -2px;
  right: -2px;

  border-top-style: none;
  border-left-style: none;

  cursor: se-resize;
}
.corner-bl {
  position: absolute;
  bottom: -2px;
  left: -2px;

  border-top-style: none;
  border-right-style: none;

  cursor: sw-resize;
}

.corner-tm {
  position: absolute;
  top: -2px;
  left: 50%;

  border-left-style: none;
  border-right-style: none;
  border-bottom-style: none;
}
.corner-bm {
  position: absolute;
  bottom: -2px;
  left: 50%;

  border-left-style: none;
  border-right-style: none;
  border-top-style: none;
}
.corner-mr {
  position: absolute;
  right: -2px;
  top: 50%;

  border-left-style: none;
  border-bottom-style: none;
  border-top-style: none;
}
.corner-ml {
  position: absolute;
  left: -2px;
  top: 50%;

  border-right-style: none;
  border-bottom-style: none;
  border-top-style: none;
}
</style>
